import { z } from 'zod';

import { idSchema, isoDateTimeSchema } from './common.js';

/**
 * Path interno do site, com leading slash. Aceita query string mas não
 * URL absoluta (proteção contra open redirect para outros domínios).
 */
const internalPathSchema = z
  .string()
  .min(1)
  .max(500)
  .regex(/^\/[^\s]*$/, {
    message: 'Caminho deve começar com `/` (ex.: /publicacoes/marco-saneamento).',
  });

/**
 * `toPath` aceita interno OU externo (alguns redirects apontam para
 * recursos externos como PDFs hospedados). Detecta protocolo http(s).
 */
const targetPathSchema = z
  .string()
  .min(1)
  .max(500)
  .refine(
    (v) => v.startsWith('/') || /^https?:\/\//.test(v),
    'Destino deve ser caminho interno (/...) ou URL absoluta (http(s)://).',
  );

export const redirectStatusCodeSchema = z.union([
  z.literal(301),
  z.literal(302),
  z.literal(307),
  z.literal(308),
]);
export type RedirectStatusCode = z.infer<typeof redirectStatusCodeSchema>;

export const redirectSchema = z.object({
  id: idSchema,
  fromPath: internalPathSchema,
  toPath: targetPathSchema,
  statusCode: redirectStatusCodeSchema,
  nota: z.string().nullable(),
  autorId: idSchema.nullable(),
  criadoEm: isoDateTimeSchema,
  atualizadoEm: isoDateTimeSchema,
});
export type Redirect = z.infer<typeof redirectSchema>;

export const createRedirectInputSchema = z.object({
  fromPath: internalPathSchema,
  toPath: targetPathSchema,
  statusCode: redirectStatusCodeSchema.optional(),
  nota: z.string().max(500).optional().or(z.literal('')),
});
export type CreateRedirectInput = z.infer<typeof createRedirectInputSchema>;

export const updateRedirectInputSchema = createRedirectInputSchema.partial();
export type UpdateRedirectInput = z.infer<typeof updateRedirectInputSchema>;

export const listRedirectsQuerySchema = z.object({
  q: z.string().max(500).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListRedirectsQuery = z.infer<typeof listRedirectsQuerySchema>;
